import { useMemo } from 'react';

import { RyeconClock, RyeconTable, RyeconTableColumn } from '@dbt-labs/sourdough';

import { ViewNode, WarehouseEntityType } from '@/__generated__/graphql';
import DetailTabs, { TabInfo } from '@/components/DetailTabs';
import { TextIconPair } from '@/pages/account/project/resource/details/components/Header';
import { CodeView } from '@/pages/account/project/resource/details/components/Tabs/CodeView';
import { ColumnsView } from '@/pages/account/project/resource/details/components/Tabs/ColumnsView';
import { formatDateWithTooltip } from '@/util/date';
import { AssetDetailsHeader } from '../AssetDetailsHeader';
import { GeneralView } from '../Tabs/GeneralView';

export const ViewDetailPage = ({ asset }: { asset: ViewNode | undefined }) => {
  const tabs: TabInfo[] = [
    { type: 'general' },
    { type: 'columns', count: asset?.fields?.length ?? 0 },
    { type: 'code' },
  ];

  const lastSyncedAtDate = asset?.lastSyncedAt
    ? new Date(asset?.lastSyncedAt)
    : undefined;
  const dateInfo = lastSyncedAtDate
    ? formatDateWithTooltip(lastSyncedAtDate)
    : undefined;
  const updatedAt = dateInfo?.display;
  const updatedAtUtc = dateInfo?.tooltip;

  const headerIcons: TextIconPair[] = useMemo(
    () => [
      {
        ryecon: RyeconTable,
        text: 'View',
      },
      ...(updatedAt
        ? [
            {
              ryecon: RyeconClock,
              text: `Last updated ${updatedAt}`,
              tooltip: updatedAtUtc || undefined,
            },
          ]
        : []),
      {
        ryecon: RyeconTableColumn,
        text: `${asset?.fields?.length ?? 0} columns`,
      },
    ],
    [updatedAt, updatedAtUtc, asset?.fields?.length],
  );

  return (
    <div>
      <AssetDetailsHeader
        urn={asset?.urn}
        qualifiedName={asset?.qualifiedName ?? ''}
        assetType={WarehouseEntityType.View}
        headerIcons={headerIcons}
        title={asset?.name ?? ''}
        platform={asset?.platform ?? null}
      />
      <DetailTabs tabs={tabs} show={true}>
        {(activeTab) => {
          return (
            <>
              {activeTab === 'general' && (
                <GeneralView asset={asset} description={asset?.description} />
              )}
              {activeTab === 'columns' && asset && (
                <ColumnsView resource={asset} shouldShowLineage={false} />
              )}
              {activeTab === 'code' && asset?.viewLogic && (
                <CodeView rawCode={asset.viewLogic} />
              )}
            </>
          );
        }}
      </DetailTabs>
    </div>
  );
};
